import { MutationResult } from '@angular-architects/ngrx-toolkit';
import { CanDeactivateFn, RouterStateSnapshot } from '@angular/router';
import { Play } from '@r1-compass-library/models';
import { Observable, of, Subscription } from 'rxjs';

import {
    FormUnsavedChangesWarningBase,
    SaveGuardCallback,
    unsavedChangesHandler,
} from '@r1-compass-library/guards/hasUnsavedChanges.guard';

/**
 * @see {PlayRecordChildFormWarning} respective interface for the children to gaurantee that this parent can call their `onSave` fns
 */
export interface PlayRecordSelectionParentFormWarning
    extends FormUnsavedChangesWarningBase<'formUnsavedChangesWarningMisc'> {
    /**
     * @description This is a special case that cannot go in the store, because the component needs to query the children
     */
    handleFormSave$: Subscription;

    /**
     * @see {PlayRecordChildFormWarning} for respective child calls
     */
    onSave(): Promise<MutationResult<void>> | Promise<MutationResult<Play>> | Promise<MutationResult<unknown>>;

    canDeactivate$: Observable<{
        dirtied: boolean;
        valid: boolean;
    }>;
}

/**
 * @description For Play Record Builder/Importer, to gaurantee that their parent,
 * which is actually tied to the route, can trigger the Builder/Importer `onSave`
 * @see {PlayRecordSelectionParentFormWarning}
 */
export interface PlayRecordChildFormWarning {
    /**
     * @description View queried by parent @see {PlayRecordSelectionParentFormWarning}
     */
    onSave(): Promise<MutationResult<void>> | Promise<MutationResult<Play>> | Promise<MutationResult<unknown>>;

    /**
     * @description Queried by parent @see {PlayRecordSelectionParentFormWarning}
     * Two forms
     * 1) Play Builder has to use a `BehaviorSubject` to track this because the form is mutated and can only be tracked after `ngOnInit`
     * 2) Play Importer uses `formUnsaved(this.playImporterForm)` because like most other forms, it is not mutated
     */
    canDeactivate$: Observable<{
        dirtied: boolean;
        valid: boolean;
    }>;
}

/**
 * @description PlayRecordSelection's parent component which has to reach into the respective section depending on the config for save data
 */
export const hasUnsavedChangesPlayRecordSelection: CanDeactivateFn<PlayRecordSelectionParentFormWarning> = (
    component: PlayRecordSelectionParentFormWarning,
    _currentRoute,
    _routerStateSnapshot,
    nextState: RouterStateSnapshot
): Observable<boolean> => {
    const responseCallback = (response: SaveGuardCallback) => {
        if (response?.type === 'confirm') {
            return component.onSave().then(() => true);
        }
        return response?.type === 'confirmWithoutSave' ? of(true) : of(false);
    };
    return unsavedChangesHandler(nextState, responseCallback, true, true);
};
